/**
 * QiblaContainer.jsx
 * ------------------
 * The single "smart" component for the Qibla card.
 *
 * All state, sensor hooks, math, side-effects (chime + haptic + hint
 * outside-click) live here — the visual sub-components below are pure
 * presentational pieces receiving props only. That mirrors the
 * successful refactor pattern used for TamamAlSalah: state stays with
 * the parent so any re-render inside the tree never resets the compass
 * smoothing, the mute preference, or the hint-popover open state.
 *
 * Composition:
 *   • QiblaHeader          — title + mute + hint buttons (+ popover)
 *   • QiblaSensorGate      — permission / unsupported banner
 *   • QiblaAlignedBanner   — the "you're aligned" pill (fades in)
 *   • QiblaScene           — Kaaba window + prayer rug + BubbleLevel
 *   • QiblaFooter          — bearing, distance, city
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { qiblaBearing, distanceToKaaba } from '@/utils/qibla';
import { useDeviceHeading } from '@/utils/useDeviceHeading';
import { useDeviceTilt } from '@/utils/useDeviceTilt';
import { getDeclinationDeg } from '@/utils/magneticDeclination';
import {
  isQiblaMuted, setQiblaMuted, signalQiblaAligned,
} from '@/utils/qiblaChime';
import { q as tq } from '@/utils/qiblaCardStrings';

import { QiblaHeader } from './QiblaHeader';
import { QiblaSensorGate } from './QiblaSensorGate';
import { QiblaAlignedBanner } from './QiblaAlignedBanner';
import { QiblaScene } from './QiblaScene';
import { QiblaFooter } from './QiblaFooter';
import { BUBBLE_MAX_TILT, BUBBLE_MAX_OFFSET } from './BubbleLevel';
import { CalibrationTip, CalibrationHintSection } from './CalibrationTip';
import {
  useCalibrationTip, isCalibrationTipEnabled, setCalibrationTipEnabled,
} from '@/utils/useCalibrationTip';

/* ── Design tokens ────────────────────────────────────────────── */
const TOLERANCE_DEG  = 3;   // absolute diff (deg) considered "aligned"
const FADE_START_DEG = 12;  // Kaaba starts fading in below this off-axis

const RTL_LANGS = new Set(['ar', 'ur', 'fa']);

/*
 * Match the rest of the app: Arabic-script languages use `-u-nu-latn`
 * so we display Latin digits (0–9) for consistency with prayer times
 * and the classic compass view.
 */
const LOCALE_MAP = {
  ar: 'ar-SA-u-nu-latn',
  en: 'en-US',
  id: 'id-ID',
  ur: 'ur-PK-u-nu-latn',
  fr: 'fr-FR',
  bn: 'bn-BD-u-nu-latn',
  zh: 'zh-CN',
  fa: 'fa-IR-u-nu-latn',
  tr: 'tr-TR',
  es: 'es-ES',
  pt: 'pt-BR',
};

const QiblaContainer = ({ location, cityLabel, surfaceColors, textColors }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isRTL = RTL_LANGS.has(lang);
  const locale = LOCALE_MAP[lang] || 'en-US';

  /* ── Local UI state ──────────────────────────────────────────── */
  const [muted, setMuted] = useState(() => isQiblaMuted());
  const [showHint, setShowHint] = useState(false);
  const prevAlignedRef = useRef(false);
  const hintRef = useRef(null);

  /* ── Qibla math (bearing + distance) ─────────────────────────── */
  const { bearing, distanceKm } = useMemo(() => {
    if (!location?.lat || !location?.lon) {
      return { bearing: null, distanceKm: null };
    }
    return {
      bearing: qiblaBearing(location.lat, location.lon),
      distanceKm: distanceToKaaba(location.lat, location.lon),
    };
  }, [location?.lat, location?.lon]);

  /* ── Magnetic declination (async fetch) ──────────────────────── */
  const [declination, setDeclination] = useState(0);
  useEffect(() => {
    let live = true;
    if (location?.lat != null && location?.lon != null) {
      getDeclinationDeg(location.lat, location.lon).then((d) => {
        if (live) setDeclination(d);
      });
    }
    return () => { live = false; };
  }, [location?.lat, location?.lon]);

  /* ── Sensor hooks ────────────────────────────────────────────── */
  const {
    heading, source, accuracy, needPermission, unsupported, requestPermission,
  } = useDeviceHeading(declination);
  const tilt = useDeviceTilt();

  /* ── Compass calibration tip ─────────────────────────────────── */
  const [calibEnabled, setCalibEnabled] = useState(() => isCalibrationTipEnabled());
  const [showHowTo, setShowHowTo] = useState(false);
  const { tip, dismiss: dismissTip } = useCalibrationTip({
    heading, source, accuracy,
    enabled: calibEnabled,
    sensorReady: !needPermission && !unsupported,
  });
  const updateCalib = (on) => {
    setCalibEnabled(on);
    setCalibrationTipEnabled(on);
    if (!on) dismissTip();
  };
  const calibLabels = {
    title: tq('calibTitle', lang),
    intro: tq('calibIntro', lang),
    steps: tq('calibSteps', lang),
    gotIt: tq('calibGotIt', lang),
    never: tq('calibNever', lang),
    ready: tq('calibReady', lang),
    toggle: tq('calibToggle', lang),
    howTo: tq('calibHowTo', lang),
  };

  /* ── Signed diff + aligned flag ──────────────────────────────── */
  const signedDiff = useMemo(() => {
    if (heading == null || bearing == null) return null;
    const rel = (((bearing - heading) % 360) + 360) % 360;
    return rel > 180 ? rel - 360 : rel;
  }, [heading, bearing]);
  const absDiff = signedDiff != null ? Math.abs(signedDiff) : null;
  const aligned = absDiff != null && absDiff <= TOLERANCE_DEG;

  /* ── Trigger chime + haptic on false→true aligned transitions ── */
  useEffect(() => {
    if (aligned && !prevAlignedRef.current) signalQiblaAligned();
    prevAlignedRef.current = aligned;
  }, [aligned]);

  /* ── Close hint popover on outside click / Escape ────────────── */
  useEffect(() => {
    if (!showHint) return undefined;
    const onDown = (e) => {
      if (hintRef.current && !hintRef.current.contains(e.target)) {
        setShowHint(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setShowHint(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [showHint]);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setQiblaMuted(next);
  };

  /* ── Kaaba fade opacity — smooth from FADE_START_DEG to TOL ──── */
  const kaabaOpacity = useMemo(() => {
    if (absDiff == null) return 0;
    if (absDiff <= TOLERANCE_DEG) return 1;
    if (absDiff >= FADE_START_DEG) return 0;
    return Math.max(
      0,
      1 - (absDiff - TOLERANCE_DEG) / (FADE_START_DEG - TOLERANCE_DEG),
    );
  }, [absDiff]);

  /* ── Bubble-level offsets from device tilt ───────────────────── */
  const bubbleOffsets = useMemo(() => {
    if (!tilt.ready) return { x: 0, y: 0 };
    const clamp = (v) => Math.max(-1, Math.min(1, v / BUBBLE_MAX_TILT));
    // gamma > 0 → right edge raised → bubble drifts right (screen +x)
    // beta  > 0 → top of phone raised → bubble drifts up  (screen -y)
    return {
      x:  clamp(tilt.gammaDeg) * BUBBLE_MAX_OFFSET,
      y: -clamp(tilt.betaDeg)  * BUBBLE_MAX_OFFSET,
    };
  }, [tilt.gammaDeg, tilt.betaDeg, tilt.ready]);

  /* ── Loading gate ────────────────────────────────────────────── */
  if (bearing == null) {
    return (
      <div
        className="p-6 md:p-8 rounded-2xl backdrop-blur-2xl border glass-surface"
        style={{
          background: surfaceColors.background,
          borderColor: surfaceColors.border,
        }}
        data-testid="qibla-compass"
      >
        <div style={{ color: textColors.secondary }}>{t('loading')}</div>
      </div>
    );
  }

  const formattedBearing  = bearing.toLocaleString('en-US', { maximumFractionDigits: 1 });
  const formattedDistance = Math.round(distanceKm).toLocaleString('en-US');

  const handleRequestPermission = () => {
    requestPermission();
    tilt.requestPermission?.();
  };

  return (
    <div
      className="rounded-2xl backdrop-blur-2xl border glass-surface overflow-hidden relative"
      style={{
        background: surfaceColors.background,
        borderColor: surfaceColors.border,
      }}
      data-testid="qibla-compass"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <QiblaHeader
        title={t('qibla')}
        muted={muted}
        showHint={showHint}
        onToggleMute={toggleMute}
        onToggleHint={() => setShowHint((v) => !v)}
        onCloseHint={() => setShowHint(false)}
        isRTL={isRTL}
        surfaceColors={surfaceColors}
        textColors={textColors}
        hintRef={hintRef}
        hintExtra={(
          <CalibrationHintSection
            enabled={calibEnabled}
            onToggle={() => updateCalib(!calibEnabled)}
            showHowTo={showHowTo}
            onToggleHowTo={() => setShowHowTo((v) => !v)}
            textColors={textColors}
            labels={calibLabels}
            isRTL={isRTL}
          />
        )}
        labels={{
          hintTitle:   tq('hintTitle',   lang),
          hintBody:    tq('hintBody',    lang),
          muteChime:   tq('muteChime',   lang),
          unmuteChime: tq('unmuteChime', lang),
          helpHint:    tq('helpHint',    lang),
        }}
      />

      <QiblaSensorGate
        unsupported={unsupported}
        needPermission={needPermission}
        onRequestPermission={handleRequestPermission}
        textColors={textColors}
        labels={{
          compassUnsupported: tq('compassUnsupported', lang),
          enableCompass:      tq('enableCompass',      lang),
        }}
      />

      <CalibrationTip
        tip={tip}
        onDismiss={dismissTip}
        onDisable={() => updateCalib(false)}
        textColors={textColors}
        labels={calibLabels}
      />

      <QiblaAlignedBanner
        aligned={aligned}
        alignedLabel={tq('aligned', lang)}
      />

      <QiblaScene
        kaabaOpacity={kaabaOpacity}
        aligned={aligned}
        tiltLevel={tilt.level}
        bubbleOffsetX={bubbleOffsets.x}
        bubbleOffsetY={bubbleOffsets.y}
        bubbleHint={tq('bubbleHint', lang)}
      />

      <QiblaFooter
        directionLabel={tq('directionLabel', lang)}
        distanceLabel={tq('distanceLabel', lang)}
        formattedBearing={formattedBearing}
        formattedDistance={formattedDistance}
        cityLabel={cityLabel}
        surfaceColors={surfaceColors}
        textColors={textColors}
      />
    </div>
  );
};

export default QiblaContainer;
